import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

/** Material UI Packages. */
import { Autocomplete, TextField, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';

import { readSocketData } from 'shared/lib/fetchSocket';
import { findTeamById, normalizeTeamOptions, pickableTeamOptions, toTeamOption } from 'shared/lib/teamOptions';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '11px',
        width: '100%',
        maxHeight: '200px',
    },
    '& .MuiAutocomplete-option': {
        padding: '5px 5px',
    },
});

/**
 * Grid edit cell for a shipment's Team (OS-566). Like the Client cell, the row holds an
 * `{ id, name }` object and the cell commits the picked team in that same shape.
 * Team is required, so the picker cannot be cleared.
 *
 * The pickable list is read from `il_team` when the cell opens — like the Client cell reads its
 * options on focus — unless the caller already holds one (`teams`).
 */
const TeamEditCell = ({ params, teams: providedTeams }) => {
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const [fetchedTeams, setFetchedTeams] = useState([]);
    const teams = providedTeams ?? fetchedTeams;

    useEffect(() => {
        if (providedTeams) return undefined;
        let active = true;
        readSocketData(dispatch, null, 'row', 'il_team', null, null, (received) => {
            if (active) setFetchedTeams(normalizeTeamOptions(received));
        });
        return () => {
            active = false;
        };
    }, [dispatch, providedTeams]);
    const currentId = params.row?.[params.field] ?? params.value;
    const options = pickableTeamOptions(teams, currentId);
    const current = findTeamById(teams, currentId);

    useEffect(() => {
        if (params.hasFocus !== false && inputRef.current) {
            inputRef.current.focus();
        }
    }, [params.hasFocus]);

    const handleChange = (event, newValue) => {
        if (!newValue) return;
        params.api.setEditCellValue({ id: params.id, field: params.field, value: toTeamOption(newValue) });
    };

    return (
        <Autocomplete
            value={current}
            options={options}
            openOnFocus
            disableClearable
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={handleChange}
            renderInput={(inputParams) => (
                <TextField
                    {...inputParams}
                    inputRef={inputRef}
                    variant="standard"
                    sx={{ px: 1, '& .MuiInputBase-root': { border: 'none' } }}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ margin: 0, padding: 0, paddingBottom: '5px' }}>
                    {option.name}
                </li>
            )}
            disablePortal={false}
            slotProps={{ popper: { component: StyledPopper } }}
            fullWidth
            sx={{
                height: '100%',
                '& .MuiInputBase-input': { fontSize: '11px' },
                '& .MuiInputBase-root': { p: 0, height: '100%' },
                '& .MuiFormControl-root': { height: '100%' },
            }}
        />
    );
};

export { TeamEditCell };
